import React, { useCallback, useEffect, useState } from "react";
import { AsyncPaginate } from "react-select-async-paginate";
import {
  ContentSection,
  StatusText,
  FooterMessage,
  ErrorMessage,
  SuccessMessage,
  CompactInt,
  asyncPaginateSelectStyles,
} from "../shared/Components";
import { Campaign, CampaignLabelingJob } from "../../appstore/models";
import {
  Badge,
  Box,
  Button,
  Container,
  ColumnLayout,
  FormField,
  Header,
  Input,
  Link,
  Modal,
  Pagination,
  SpaceBetween,
  TextFilter,
  Table,
} from "@awsui/components-react";
import styled from "styled-components";
import {
  linkCampaignLabelingJob,
  listGroundTruthLabelingJobs,
  listCampaignLabelingJobs,
} from "../../common/api";
import appConfig from "../../common/appConfig";
import { humanizeDuration } from "../../common/helpers";
import { DynamoDB } from "aws-sdk";
import { LabelingJobSummary } from "@aws-sdk/client-sagemaker";

const TableHeader = styled.div`
  justify-content: space-between;
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  line-height: 22px;
  &:first-child {
    font-weight: 700;
    font-size: 18px;
  }
`;

export interface LinkCampaignLabelingJobProps {
  onCancel: () => void;
  onSuccess: () => void;
  campaign: Campaign;
  visible: boolean;
}

export const LinkCampaignLabelingJobModal = (
  props: LinkCampaignLabelingJobProps
) => {
  const { onCancel, onSuccess, campaign, visible } = props;
  const [creating, setCreating] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [apiSuccess, setApiSuccess] = useState(false);
  const [
    selectedGroundTruthLabelingJobOption,
    setSelectedGroundTruthLabelingJobOption,
  ] = useState<
    | {
        value: string;
        label: string;
      }
    | undefined
  >();
  const [query, setQuery] = useState<string>("");

  const [
    groundTruthLabelingJobSummariesByName,
    setGroundTruthLabelingJobSummariesByName,
  ] = useState<{
    [key: string]: LabelingJobSummary;
  }>({});

  const [nextToken, setNextToken] = useState<string | undefined>(undefined);

  const [selectedGroundTruthLabelingJob, setSelectedGroundTruthLabelingJob] =
    useState<LabelingJobSummary | undefined>(undefined);

  useEffect(() => {
    if (!selectedGroundTruthLabelingJobOption) {
      return;
    }
    if (
      selectedGroundTruthLabelingJob &&
      selectedGroundTruthLabelingJob.LabelingJobName ===
        selectedGroundTruthLabelingJobOption.value
    ) {
      return;
    }
    setSelectedGroundTruthLabelingJob(
      groundTruthLabelingJobSummariesByName[
        selectedGroundTruthLabelingJobOption.value
      ]
    );
  }, [
    groundTruthLabelingJobSummariesByName,
    selectedGroundTruthLabelingJob,
    selectedGroundTruthLabelingJobOption,
  ]);

  const loadGroundTruthJobOptions = useCallback(
    async (newQuery: string) => {
      const response = await listGroundTruthLabelingJobs({
        NameContains: newQuery ? newQuery : undefined,
        NextToken: query === newQuery ? nextToken : undefined,
      });
      setQuery(newQuery);
      setNextToken(response.NextToken);
      const newGroundTruthLabelingJobSummariesByName: {
        [key: string]: LabelingJobSummary;
      } = {};
      response.groundTruthLabelingJobSummaries.forEach((summary) => {
        if (!summary.LabelingJobName) {
          return;
        }
        newGroundTruthLabelingJobSummariesByName[summary.LabelingJobName] =
          summary;
      });
      setGroundTruthLabelingJobSummariesByName({
        ...groundTruthLabelingJobSummariesByName,
        ...newGroundTruthLabelingJobSummariesByName,
      });
      return {
        options: response.groundTruthLabelingJobSummaries.map(
          (groundTruthLabelingJob) => ({
            label: groundTruthLabelingJob.LabelingJobName,
            value: groundTruthLabelingJob.LabelingJobName,
          })
        ),
        hasMore: response.NextToken !== undefined,
        additional: true,
      };
    },
    [nextToken, groundTruthLabelingJobSummariesByName, query]
  );

  const create = useCallback(async () => {
    if (
      !selectedGroundTruthLabelingJob ||
      !selectedGroundTruthLabelingJob.LabelingJobName
    ) {
      return;
    }
    setCreating(true);
    setApiSuccess(false);
    setErrors([]);
    const linkCampaignLabelingJobResult = await linkCampaignLabelingJob({
      campaignName: campaign.JobName,
      groundTruthLabelingJobName:
        selectedGroundTruthLabelingJob.LabelingJobName,
    });
    if (!linkCampaignLabelingJobResult.success) {
      setErrors(
        linkCampaignLabelingJobResult.errors || [
          "Error linking campaign to labeling job",
        ]
      );
      setCreating(false);
      setApiSuccess(false);
      return;
    }
    setCreating(false);
    setApiSuccess(true);
    onSuccess();
  }, [selectedGroundTruthLabelingJob, campaign.JobName, onSuccess]);
  return (
    <Modal
      visible={visible}
      onDismiss={() => onCancel()}
      header="Link Ground Truth Labeling Job to Campaign"
      closeAriaLabel="Close dialog"
      footer={
        <div className="awsui-util-f-r">
          {errors ? (
            <FooterMessage>
              {errors.map((message, i) => (
                <ErrorMessage key={i} message={message} />
              ))}
            </FooterMessage>
          ) : null}
          {apiSuccess ? (
            <FooterMessage>
              <SuccessMessage>Success</SuccessMessage>
            </FooterMessage>
          ) : null}
          <Button variant="link" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={selectedGroundTruthLabelingJob === null || creating}
            onClick={() => create()}
          >
            {creating ? "Linking Labeling Job..." : "Link Labeling Job"}
          </Button>
        </div>
      }
    >
      <ColumnLayout borders="horizontal">
        <SpaceBetween size="l">
          <FormField label="Ground Truth Labeling Job">
            <AsyncPaginate
              styles={asyncPaginateSelectStyles}
              onChange={function (option: { value: string; label: string }) {
                setSelectedGroundTruthLabelingJobOption(option);
              }}
              value={selectedGroundTruthLabelingJobOption}
              loadOptions={loadGroundTruthJobOptions}
            ></AsyncPaginate>
          </FormField>
          {selectedGroundTruthLabelingJob ? (
            <>
              <FormField label="Input Manifest S3 URI">
                <Input
                  disabled={true}
                  value={
                    selectedGroundTruthLabelingJob.InputConfig?.DataSource
                      ?.S3DataSource?.ManifestS3Uri || ""
                  }
                />
              </FormField>
            </>
          ) : null}
        </SpaceBetween>
      </ColumnLayout>
    </Modal>
  );
};

const CampaignLabelingJobsTable = (props: { campaign: Campaign }) => {
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const { campaign } = props;
  const pageSize = 8;
  const [campaignLabelingJobs, setCampaignLabelingJobs] = useState<
    CampaignLabelingJob[]
  >([]);
  const [loadingError, setLoadingError] = useState<undefined | string>(
    undefined
  );
  const [showNewLabelingJob, setShowNewLabelingJob] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [searchTriggered, setSearchTriggered] = useState(false);
  const [pageCount, setPageCount] = useState(1);
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState<
    DynamoDB.Key | undefined
  >(undefined);

  const fetchPage = (pageNumber: number) => {
    if (pageNumber === currentPageIndex) {
      return;
    }

    setCurrentPageIndex(pageNumber);
    if (pageNumber === pageCount && hasNextPage) {
      setSearchTriggered(true);
    }
  };
  const refresh = () => {
    setCurrentPageIndex(1);
    setCampaignLabelingJobs([]);
    setHasNextPage(true);
    setPageCount(1);
    setLastEvaluatedKey(undefined);
    setSearchTriggered(true);
  };

  const loadCampaignLabelingJobs = useCallback(async () => {
    if (!hasNextPage) {
      return;
    }
    setLoadingError(undefined);
    try {
      const listCampaignLabelingJobsResult = await listCampaignLabelingJobs(
        campaign.JobName,
        pageSize,
        lastEvaluatedKey
      );
      setLastEvaluatedKey(listCampaignLabelingJobsResult.LastEvaluatedKey);
      setPageCount(
        listCampaignLabelingJobsResult.LastEvaluatedKey
          ? pageCount + 1
          : pageCount
      );
      setCampaignLabelingJobs([
        ...campaignLabelingJobs,
        ...listCampaignLabelingJobsResult.campaignLabelingJobs,
      ]);
      if (listCampaignLabelingJobsResult.LastEvaluatedKey) {
        setHasNextPage(true);
      } else {
        setHasNextPage(false);
      }
    } catch (e) {
      console.log("Error loading Labeling results", e);
      setLoadingError("Error loading Labeling results");
    }
  }, [
    campaign,
    campaignLabelingJobs,
    pageCount,
    hasNextPage,
    lastEvaluatedKey,
  ]);

  useEffect(() => {
    if (searchTriggered) {
      loadCampaignLabelingJobs();
      setSearchTriggered(false);
    }
  }, [searchTriggered, loadCampaignLabelingJobs]);

  useEffect(() => {
    setSearchTriggered(true);
  }, []);

  if (loadingError) {
    return (
      <ContentSection>
        <Container header={<Header variant="h2">Labeling Summary</Header>}>
          <div>{loadingError}</div>
        </Container>
      </ContentSection>
    );
  }

  return (
    <ContentSection>
      <Table
        trackBy="CampaignLabelingId"
        columnDefinitions={[
          {
            id: "Status",
            header: "Status",
            cell: (e) => {
              const statusCode =
                e.Status === "Completed"
                  ? "success"
                  : e.Status === "Failed"
                  ? "error"
                  : "running";
              return (
                <StatusText key={e.CampaignJobName} status={statusCode}>
                  {e.Status}
                </StatusText>
              );
            },
            sortingField: "Status",
          },
          {
            id: "GroundTruthLabelingJob",
            header: "Ground Truth Labeling Job",
            cell: (e) => {
              if (e.GroundTruthLabelingJobName) {
                const groundTruthJobName = e.GroundTruthJobArn.split("/").pop();
                const groundTruthJobArnUrl = new URL(
                  `https://${appConfig.consoleHostname}/sagemaker/groundtruth?region=${appConfig.awsRegion}#/labeling-jobs/details/${groundTruthJobName}`
                );
                return (
                  <Link
                    href={groundTruthJobArnUrl.href}
                    key={e.CampaignJobName}
                    target="_blank"
                  >
                    {e.CampaignJobName}
                  </Link>
                );
              }
              return e.CampaignJobName;
            },
            sortingField: "CampaignJobName",
          },
          {
            id: "DateCreated",
            header: "Date Created",
            cell: (e) => e.DateCreated,
            sortingField: "DateCreated",
          },
          {
            id: "DateCompleted",
            header: "Date Completed",
            cell: (e) => e.DateCompleted,
            sortingField: "DateCompleted",
          },
          {
            id: "Duration",
            header: "Duration",
            cell: (e) => {
              if (!e.DateCompleted && e.Status === "InProgress") {
                const durationMs =
                  new Date().getTime() -
                  new Date(`${e.DateCreated}Z`).getTime();
                return humanizeDuration(durationMs);
              }
              if (!e.DurationSeconds) {
                return "";
              }
              return humanizeDuration(e.DurationSeconds * 1000);
            },
            sortingField: "Duration",
          },
          {
            id: "WorkersPerChip",
            header: "Workers Per Chip",
            cell: (e) => e.NumberOfHumanWorkersPerDataObject || 1,
            sortingField: "WorkersPerChip",
          },
          {
            id: "FeatureClasses",
            header: "FeatureClasses",
            cell: (e) => {
              return (
                <>
                  {e.FeatureClasses.map((featureClass, featureClassIndex) => (
                    <Badge color="blue" key={featureClassIndex}>
                      {featureClass}
                    </Badge>
                  ))}
                </>
              );
            },
            sortingField: "FeatureClasses",
          },
          {
            id: "TotalFeaturesLabeled",
            header: "Total Features Labeled",
            cell: (e) => {
              return <CompactInt value={e.FeaturesLabeledCount} />;
            },
            sortingField: "TotalFeaturesLabeled",
          },
          {
            id: "ChipsLabeled",
            header: "Chips Labeled",
            cell: (e) => {
              return <CompactInt value={e.ChipsLabeled} />;
            },
            sortingField: "TotalFeaturesLabeled",
          },
          {
            id: "ChipsRemaining",
            header: "Chips Remaining",
            cell: (e) => {
              return <CompactInt value={e.ChipsRemaining} />;
            },
            sortingField: "TotalFeaturesLabeled",
          },
          {
            id: "ErrorMessage",
            header: "Error Message",
            cell: (e) => e.ErrorMessage,
            sortingField: "ErrorMessage",
          },
        ]}
        items={campaignLabelingJobs.slice(
          (currentPageIndex - 1) * pageSize,
          currentPageIndex * pageSize
        )}
        loadingText="Loading jobs"
        visibleColumns={[
          "Status",
          "GroundTruthLabelingJob",
          "DateCreated",
          "DateCompleted",
          "Duration",
          "WorkersPerChip",
          "FeatureClasses",
          "TotalFeaturesLabeled",
          "ChipsLabeled",
          "ChipsRemaining",
          "ErrorMessage",
        ]}
        empty={
          <Box textAlign="center" color="inherit">
            <b>No jobs</b>
            <Box padding={{ bottom: "s" }} variant="p" color="inherit">
              No jobs to display.
            </Box>
          </Box>
        }
        filter={
          <TextFilter filteringText="" filteringPlaceholder="Find resources" />
        }
        header={
          <TableHeader>
            Labeling Jobs
            <Button
              variant="primary"
              onClick={() => setShowNewLabelingJob(true)}
            >
              Link Labeling Job
            </Button>
          </TableHeader>
        }
        pagination={
          <Pagination
            openEnd={hasNextPage}
            currentPageIndex={currentPageIndex}
            onChange={({ detail }) => fetchPage(detail.currentPageIndex)}
            pagesCount={pageCount}
            ariaLabels={{
              nextPageLabel: "Next page",
              previousPageLabel: "Previous page",
              pageLabel: (pageNumber) => `Page ${pageNumber} of all pages`,
            }}
          />
        }
      />
      <LinkCampaignLabelingJobModal
        campaign={campaign}
        onSuccess={() => {
          setShowNewLabelingJob(false);
          refresh();
        }}
        onCancel={() => setShowNewLabelingJob(false)}
        visible={showNewLabelingJob}
      />
    </ContentSection>
  );
};

export default CampaignLabelingJobsTable;
